// RightPinPane — 320 px right pin pane per UI_SPEC §3.1.
//
// What goes here is view-specific. Views pass a `rightPane` ReactNode to
// Shell; the default if no view supplies one is:
//   - selection detail (property-grid of the currently-selected entity)
//   - pinned artifacts (mini-previews from chat-driven pins)
//   - recent activity (timeline)
//
// "Chat is not in this pane" — chat lives in the floating pill (§1.18).

import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ArtifactView } from "./ArtifactView.js";
import { envelopeRoute } from "../lib/routes.js";
import { useShell } from "./ShellContext.js";

export function RightPinPane({ children }: { children?: ReactNode }) {
  const { selected, chat, setSelected, viewRightPane } = useShell();
  const navigate = useNavigate();

  return (
    <aside className="overflow-y-auto border-l border-slate-200 bg-white p-3 text-sm">
      {/* Per-view content first (set via useRightPane). */}
      {viewRightPane}

      {/* Children passed by Shell — kept for back-compat / future use. */}
      {children}

      {/* Selected entity (e.g. property-grid from get_task). */}
      {selected && (
        <section className="mt-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="label">Selected</div>
            <button
              type="button"
              className="text-[10px] text-slate-400 hover:text-slate-600"
              onClick={() => setSelected(null)}
            >clear</button>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50">
            <ArtifactView envelope={selected.envelope} />
          </div>
        </section>
      )}

      {/* Pinned artifacts surfaced from chat (primary-pane envelopes). */}
      {chat.pinboard.length > 0 && (
        <section className="mt-3">
          <div className="label mb-2">Pinned</div>
          <div className="space-y-2">
            {chat.pinboard.slice(-4).map((p) => {
              const target = envelopeRoute(p.env);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="block w-full rounded border border-slate-200 p-2 text-left hover:bg-slate-50"
                  title={target ? `Open in ${target}` : undefined}
                  onClick={() => {
                    chat.setActivePinboard(p.id);
                    if (target) navigate(target);
                  }}
                >
                  <div className="label">{p.env.artifact_type}</div>
                  <div className="mt-1 truncate text-xs text-slate-700">{p.env.title}</div>
                  <div className="text-[10px] text-slate-500">
                    {p.env.audit?.tool} · {p.env.audit?.freshness ?? "?"}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Tables surfaced from chat (secondary-pane envelopes). */}
      {chat.sidekick.length > 0 && (
        <section className="mt-3">
          <div className="label mb-2">Tables from chat</div>
          <div className="space-y-2">
            {chat.sidekick.slice(-4).map((p) => {
              const target = envelopeRoute(p.env);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="block w-full rounded border border-slate-200 p-2 text-left hover:bg-slate-50"
                  title={target ? `Open in ${target}` : undefined}
                  onClick={() => {
                    chat.setActiveSidekick(p.id);
                    if (target) navigate(target);
                  }}
                >
                  <div className="label">{p.env.artifact_type}</div>
                  <div className="mt-1 truncate text-xs text-slate-700">{p.env.title}</div>
                  <div className="text-[10px] text-slate-500">{p.env.audit?.tool}</div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Empty hint removed — Shell hides this pane entirely when there is
          no content, so this fallback is unreachable. */}
    </aside>
  );
}
